import {
    RedirectHomePage,
    RedirectGamePage,
    createRedirectComponent,
} from "@/components/base/Redirect"
import ApiSignals, { ApiSignalFetchers } from "@/apis/api_signals"
import { PATHS } from "@/constants/path"
import { Auth } from "@/features_signal/auth"
import { CustomError } from "@/utils/error"
import { RouteObject } from "react-router-dom"
import { toast } from "react-toastify"
import { createLogger } from "@/utils/logger"
import AppSignals from "@/features_signal/app"

const logger = createLogger("router_redirect")

const route_to_redirect_glife_mini_program: RouteObject = {
    path: PATHS.GLIFE_REDIRECT_GLIFE_MINI_PROGRAM,
    async lazy() {
        const redirect_url = "gcash://com.mynt.gcash/app/006300121300?appId=2170020210553950"
        function isMobile() {
            // UA 判斷 + 螢幕寬度判斷（900px 以下視為 mobile）
            const ua = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                navigator.userAgent,
            )
            const isSmallScreen = window.innerWidth < 900
            return ua && isSmallScreen
        }
        function isRedirectToGlifeMiniProgram() {
            return window.location.pathname === PATHS.GLIFE_REDIRECT_GLIFE_MINI_PROGRAM
        }
        // Directly redirect to the GCash app

        if (isMobile() && isRedirectToGlifeMiniProgram()) {
            setTimeout(() => {
                window.location.href = redirect_url
            }, 1000)
        }

        return {
            Component: RedirectHomePage,
        }
    },
}

const route_to_redirect_gcash_redirect_website: RouteObject = {
    path: PATHS.GLIFE_REDIRECT_GCASH_REDIRECT_WEBSITE,
    async lazy() {
        const redirect_url =
            "https://gcashapp.page.link/?link=https://gcash.splashscreen/?redirect%3Dgcash%3A%2F%2Fcom.mynt.gcash%2Fapp%2F006300121300%3FappId%3D2170020210553950%2526page%253Dpages%252Findex%252Findex%2526apn%253Dcom.globe.gcash.android%2526isi%253D520020791%2526ibi%253Dcom.globetel.gcash&apn=com.globe.gcash.android&isi=520020791&ibi=com.globetel.gcash"
        function isMobile() {
            // UA 判斷 + 螢幕寬度判斷（900px 以下視為 mobile）
            const ua = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                navigator.userAgent,
            )
            const isSmallScreen = window.innerWidth < 900
            return ua && isSmallScreen
        }
        function isRedirectToGlifeMiniProgram() {
            return window.location.pathname === PATHS.GLIFE_REDIRECT_GCASH_REDIRECT_WEBSITE
        }
        // Directly redirect to the GCash app
        if (isMobile() && isRedirectToGlifeMiniProgram()) {
            window.location.href = redirect_url
        }

        return {
            Component: RedirectHomePage,
        }
    },
}

const route_from_glife: RouteObject = {
    path: PATHS.GLIFE_REDIRECT_USER,
    async lazy() {
        AppSignals.setGlifeUser(true)
        AppSignals.turnOffKycRouterChecker()
        Auth.turnOffAccessTokenSignalSubscribe()
        const searchParams = new URLSearchParams(location.search)
        const glife_token = searchParams.get("token")
        const game_code = searchParams.get("game")
        const final_token = (() => (glife_token ? atob(glife_token) : null))()
        let finalMessage: string | null = null
        let isError = false
        try {
            if (final_token) {
                Auth.setAccessToken(final_token.trim())
                await Promise.all([
                    ApiSignalFetchers.fetchDataSignalProfile(),
                    ApiSignalFetchers.fetchDataSignalKyc(),
                    ApiSignalFetchers.fetchWalletSignal(),
                ])
            }
            if (game_code) {
                const Component = createRedirectComponent(RedirectGamePage, {
                    gameName: game_code,
                })
                return {
                    Component,
                }
            }
        } catch (error) {
            isError = true
            if (error instanceof CustomError) {
                finalMessage = `Glife login failed: ${error.message}`
            } else {
                finalMessage = "Glife login failed"
            }
        } finally {
            AppSignals.turnOnKycRouterChecker()
            Auth.turnOnAccessTokenSignalSubscribe()
            if (finalMessage) {
                const t = isError ? toast.error : toast.success
                t(finalMessage)
            }
        }
        return {
            Component: RedirectHomePage,
        }
    },
}

const route_from_lazadamini: RouteObject = {
    path: PATHS.LAZADAMINI_REDIRECT,
    async lazy() {
        AppSignals.setLazadaminiUser(true)
        AppSignals.turnOffKycRouterChecker()
        Auth.turnOffAccessTokenSignalSubscribe()
        const searchParams = new URLSearchParams(location.search)
        const lazadamini_token = searchParams.get("authCode")
        let finalMessage: string | null = null
        let isError = false
        try {
            if (lazadamini_token) {
                // const loginResult = await ApiSignalFetchers.fetchDataSignalLazadaminiLogin({ code: lazadamini_token })
                const res = await ApiSignalFetchers.fetchDataSignalLazadaminiLogin({
                    code: lazadamini_token,
                })
                const loginResult = ApiSignals.dataSignalLazadaminiLogin.value
                if (loginResult?.token) {
                    Auth.setAccessToken(loginResult.token)
                    await Promise.all([
                        ApiSignalFetchers.fetchDataSignalProfile(),
                        ApiSignalFetchers.fetchDataSignalKyc(),
                        ApiSignalFetchers.fetchWalletSignal(),
                    ])
                }
            }
        } catch (error) {
            isError = true
            if (error instanceof CustomError) {
                finalMessage = `Lazadamini login failed: ${error.message}`
            } else {
                finalMessage = "Lazadamini login failed"
            }
        } finally {
            AppSignals.turnOnKycRouterChecker()
            Auth.turnOnAccessTokenSignalSubscribe()
            if (finalMessage) {
                const t = isError ? toast.error : toast.success
                t(finalMessage)
            }
        }
        return {
            Component: RedirectHomePage,
        }
    },
}

const route_from_mayamini: RouteObject = {
    path: PATHS.MAYAMINI_REDIRECT,
    async lazy() {
        AppSignals.setMayaminiUser(true)
        AppSignals.turnOffKycRouterChecker()
        Auth.turnOffAccessTokenSignalSubscribe()
        const searchParams = new URLSearchParams(location.search)
        const mayamini_token = searchParams.get("sessionId")
        const Mayamini_Code = mayamini_token
            ? new TextDecoder().decode(
                  Uint8Array.from(atob(mayamini_token), (c) => c.charCodeAt(0)),
              )
            : ""
        let finalMessage: string | null = null
        let isError = false
        console.log("Mayamini_Code---------", mayamini_token, Mayamini_Code)
        try {
            if (Mayamini_Code) {
                Auth.setAccessToken(Mayamini_Code)
                await Promise.all([
                    ApiSignalFetchers.fetchDataSignalProfile(),
                    ApiSignalFetchers.fetchDataSignalKyc(),
                    ApiSignalFetchers.fetchWalletSignal(),
                ])
            }
        } catch (error) {
            isError = true
            if (error instanceof CustomError) {
                finalMessage = `Mayamini login failed: ${error.message}`
            } else {
                finalMessage = "Mayamini login failed"
            }
        } finally {
            AppSignals.turnOnKycRouterChecker()
            Auth.turnOnAccessTokenSignalSubscribe()
            if (finalMessage) {
                const t = isError ? toast.error : toast.success
                t(finalMessage)
            }
        }
        return {
            Component: RedirectHomePage,
        }
    },
}

const ROUTES_REDIRECT_RAW: RouteObject[] = [
    //
    route_from_glife,
    route_from_lazadamini,
    route_from_mayamini,
    route_to_redirect_glife_mini_program,
    route_to_redirect_gcash_redirect_website,
]

export const ROUTES_REDIRECT = ROUTES_REDIRECT_RAW.map((route) => {
    if (location.pathname.match(route.path ?? "")) {
        return {
            ...route,
            path: route.path ?? "",
        }
    }
    return null
}).filter((route) => route !== null) as RouteObject[]
